/**
 * FlowIntelligence — the operational heartbeat panel for the Deal Room.
 *
 * Renders the live `/api/deals/{id}/intelligence` payload as a calm, dark
 * cinematic card matching ChainFlow's premium operational identity. NOT a
 * dashboard widget. NOT a chatbot. A live transaction signal engine.
 */
import React from "react";
import {
  Sparkles, ArrowUpRight, TrendingUp, TrendingDown, Minus, Clock,
  FileText, KeyRound, ShieldAlert, Users, Activity, Link2,
} from "lucide-react";
import { useNewItemTracker } from "../../hooks/useNewItemTracker";

const TONE = {
  high: { fg: "#FCA5A5", dot: "#EF4444", chip: "rgba(239,68,68,0.14)", border: "rgba(239,68,68,0.28)" },
  med:  { fg: "#FCD34D", dot: "#F59E0B", chip: "rgba(245,158,11,0.14)", border: "rgba(245,158,11,0.28)" },
  low:  { fg: "#A7F3D0", dot: "#10B981", chip: "rgba(16,185,129,0.14)", border: "rgba(16,185,129,0.28)" },
};

const ICONS = {
  user: Users, file: FileText, key: KeyRound, shield: ShieldAlert,
  chain: Link2, spark: Sparkles, clock: Clock, activity: Activity,
};

const MomentumGlyph = ({ token }) => {
  if (token === "accelerating") return <TrendingUp size={11} />;
  if (token === "slowing" || token === "at_risk") return <TrendingDown size={11} />;
  return <Minus size={11} />;
};

const momentumStyle = (token) => {
  if (token === "accelerating") return { fg: "#A7F3D0", dot: "#10B981", chip: "rgba(16,185,129,0.18)", border: "rgba(16,185,129,0.32)" };
  if (token === "stable")       return { fg: "#C4B5FD", dot: "#8B5CF6", chip: "rgba(139,92,246,0.18)", border: "rgba(139,92,246,0.32)" };
  if (token === "slowing")      return { fg: "#FCD34D", dot: "#F59E0B", chip: "rgba(245,158,11,0.18)", border: "rgba(245,158,11,0.32)" };
  return { fg: "#FCA5A5", dot: "#EF4444", chip: "rgba(239,68,68,0.18)", border: "rgba(239,68,68,0.32)" };
};

/** Compact momentum chip — visible in Deal Room header, pipeline cards and dashboard. */
export const MomentumChip = ({ token = "stable", label, size = "md", testid }) => {
  const s = momentumStyle(token);
  const t = label || (token[0].toUpperCase() + token.slice(1));
  const h = size === "sm" ? "h-6" : "h-7";
  const px = size === "sm" ? "px-2" : "px-2.5";
  const fs = size === "sm" ? "text-[10.5px]" : "text-[11.5px]";
  return (
    <span
      data-testid={testid || `momentum-chip-${token}`}
      className={`${h} ${px} ${fs} inline-flex items-center gap-1.5 rounded-full font-bold tracking-tight tabular-nums`}
      style={{ background: s.chip, border: `1px solid ${s.border}`, color: s.fg }}
      title={`Flow Signal™ · ${t}`}
    >
      <span className="relative inline-flex">
        <span className="w-1.5 h-1.5 rounded-full" style={{ background: s.dot }} />
        {token === "accelerating" && (
          <span className="absolute inset-0 rounded-full animate-ping" style={{ background: s.dot, opacity: 0.6 }} />
        )}
      </span>
      <MomentumGlyph token={token} />
      {t}
    </span>
  );
};

/** Tiny score donut — 28px ring, used inside subtle stat cells. */
const TinyRing = ({ value, color = "#8B5CF6" }) => {
  const v = Math.max(0, Math.min(100, value || 0));
  const r = 11;
  const c = 2 * Math.PI * r;
  const dash = (v / 100) * c;
  return (
    <svg viewBox="0 0 28 28" width={28} height={28} className="-rotate-90">
      <circle cx="14" cy="14" r={r} stroke="rgba(148,163,184,0.18)" strokeWidth="2.4" fill="none" />
      <circle cx="14" cy="14" r={r} stroke={color} strokeWidth="2.4" fill="none" strokeLinecap="round" strokeDasharray={`${dash} ${c}`} />
    </svg>
  );
};

const StatRow = ({ icon: Icon, label, sub, valueNode, accent = "#C4B5FD" }) => (
  <div className="flex items-center gap-3 py-2.5">
    <span className="w-7 h-7 rounded-lg flex items-center justify-center shrink-0"
      style={{ background: "rgba(139,92,246,0.10)", color: accent, border: "1px solid rgba(139,92,246,0.22)" }}>
      <Icon size={12} />
    </span>
    <div className="flex-1 min-w-0">
      <p className="text-[11.5px] font-semibold text-white leading-tight truncate">{label}</p>
      <p className="text-[10.5px] mt-0.5 truncate" style={{ color: "rgba(148,163,184,0.85)" }}>{sub}</p>
    </div>
    <div className="shrink-0">{valueNode}</div>
  </div>
);

const Score = ({ value, label, tone = "low" }) => {
  const t = TONE[tone] || TONE.low;
  return (
    <div className="flex items-center gap-1.5">
      <span className="font-bold text-[12px] tabular-nums" style={{ color: t.fg }}>{value}</span>
      <span className="text-[10px] uppercase tracking-[0.16em] font-bold" style={{ color: t.fg, opacity: 0.85 }}>{label}</span>
    </div>
  );
};

const RiskTrend = ({ trend, delta }) => {
  const map = {
    improving: { fg: "#A7F3D0", icon: TrendingDown, label: "Improving" },
    declining: { fg: "#FCA5A5", icon: TrendingUp,   label: "Declining" },
    stable:    { fg: "#C4B5FD", icon: Minus,        label: "Stable" },
  };
  const m = map[trend] || map.stable;
  const Icon = m.icon;
  return (
    <span className="inline-flex items-center gap-1 text-[10.5px] font-bold" style={{ color: m.fg }}>
      <Icon size={11} />
      {m.label}{delta ? ` · ${delta > 0 ? "+" : ""}${delta}` : ""}
    </span>
  );
};

const Alert = ({ alert, isNew }) => {
  const t = TONE[alert.tone] || TONE.low;
  const Icon = ICONS[alert.icon] || Sparkles;
  return (
    <li
      data-testid={`flow-intel-alert-${alert.id}`}
      data-new={isNew ? "true" : undefined}
      className={`flex items-start gap-3 px-3 py-2.5 rounded-xl transition-colors ${isNew ? "cf-settle-dark" : ""}`}
      style={{ background: "rgba(255,255,255,0.02)", border: "1px solid rgba(255,255,255,0.06)" }}
    >
      <span className="w-7 h-7 rounded-lg flex items-center justify-center shrink-0 mt-0.5 relative"
        style={{ background: t.chip, color: t.fg, border: `1px solid ${t.border}` }}>
        <Icon size={11} />
        {isNew && (
          <span
            aria-hidden="true"
            className="cf-new-dot absolute -top-0.5 -right-0.5 w-1.5 h-1.5 rounded-full"
            style={{ background: "#A78BFA" }}
          />
        )}
      </span>
      <div className="flex-1 min-w-0">
        <p className="text-[11.5px] font-bold text-white leading-tight">{alert.title}</p>
        <p className="text-[10.5px] mt-0.5 leading-snug" style={{ color: "rgba(148,163,184,0.95)" }}>{alert.detail}</p>
      </div>
    </li>
  );
};

/** Subtle ring — large probability donut. */
const ProbRing = ({ value, label, sub }) => {
  const v = Math.max(0, Math.min(100, value || 0));
  const r = 28;
  const c = 2 * Math.PI * r;
  const dash = (v / 100) * c;
  const color = v >= 75 ? "#22C55E" : v >= 55 ? "#8B5CF6" : v >= 35 ? "#F59E0B" : "#EF4444";
  return (
    <div className="flex items-center gap-3">
      <div className="relative w-[64px] h-[64px]">
        <svg viewBox="0 0 64 64" className="w-full h-full -rotate-90">
          <circle cx="32" cy="32" r={r} stroke="rgba(148,163,184,0.18)" strokeWidth="4" fill="none" />
          <circle cx="32" cy="32" r={r} stroke={color} strokeWidth="4" fill="none" strokeLinecap="round" strokeDasharray={`${dash} ${c}`} />
        </svg>
        <div className="absolute inset-0 flex items-center justify-center">
          <span className="text-[18px] font-bold tabular-nums text-white">{v}</span>
        </div>
      </div>
      <div className="min-w-0">
        <p className="text-[10px] uppercase tracking-[0.20em] font-bold" style={{ color: "#C4B5FD" }}>{label}</p>
        <p className="text-[12.5px] font-bold text-white leading-tight">{sub}</p>
      </div>
    </div>
  );
};

export const FlowIntelligencePanel = ({ intel, loading, deal }) => {
  const alerts = (intel && intel.operational_alerts) || [];
  const newAlertIds = useNewItemTracker(alerts.map((a) => a.id));
  if (loading || !intel) {
    return (
      <section
        className="rounded-2xl p-5 overflow-hidden"
        style={{
          background: "linear-gradient(180deg, #1A1635 0%, #0F0E22 100%)",
          border: "1px solid rgba(139,92,246,0.28)",
          boxShadow: "0 16px 36px -16px rgba(99,102,241,0.45), inset 0 1px 0 rgba(255,255,255,0.06)",
        }}
        data-testid="flow-intelligence-panel"
      >
        <div className="flex items-center gap-2 mb-4">
          <span className="w-7 h-7 rounded-md inline-flex items-center justify-center"
            style={{ background: "rgba(139,92,246,0.22)", color: "#C4B5FD" }}>
            <Sparkles size={12} />
          </span>
          <p className="text-[11px] font-bold uppercase tracking-[0.22em] text-white/80">Flow Intelligence</p>
        </div>
        <div className="space-y-2 cf-pulse">
          <div className="h-12 rounded-lg" style={{ background: "rgba(255,255,255,0.04)" }} />
          <div className="h-8 rounded-lg" style={{ background: "rgba(255,255,255,0.03)" }} />
          <div className="h-8 rounded-lg" style={{ background: "rgba(255,255,255,0.03)" }} />
          <div className="h-8 rounded-lg" style={{ background: "rgba(255,255,255,0.03)" }} />
        </div>
      </section>
    );
  }

  const sol = intel.solicitor_response || {};
  const buyer = intel.buyer_engagement || {};
  const chain = intel.chain_stability || {};

  return (
    <section
      className="relative rounded-2xl p-5 overflow-hidden"
      style={{
        background: "linear-gradient(180deg, #1A1635 0%, #0F0E22 100%)",
        border: "1px solid rgba(139,92,246,0.28)",
        boxShadow: "0 16px 36px -16px rgba(99,102,241,0.45), inset 0 1px 0 rgba(255,255,255,0.06)",
      }}
      data-testid="flow-intelligence-panel"
    >
      {/* Ambient glow */}
      <span aria-hidden="true" className="absolute -top-12 -right-12 w-44 h-44 rounded-full blur-3xl pointer-events-none"
        style={{ background: "radial-gradient(circle, rgba(139,92,246,0.45), transparent 70%)" }} />
      <span aria-hidden="true" className="absolute -bottom-16 -left-12 w-48 h-48 rounded-full blur-3xl pointer-events-none"
        style={{ background: "radial-gradient(circle, rgba(99,102,241,0.30), transparent 70%)" }} />

      <div className="relative">
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <div className="inline-flex items-center gap-2">
            <span className="w-7 h-7 rounded-md inline-flex items-center justify-center"
              style={{ background: "rgba(139,92,246,0.22)", color: "#C4B5FD" }}>
              <Sparkles size={12} />
            </span>
            <div>
              <p className="text-[10.5px] font-bold uppercase tracking-[0.22em] text-white">Flow Intelligence</p>
              <p className="text-[9.5px] uppercase tracking-[0.18em] text-white/50">Live · operational signals</p>
            </div>
          </div>
          <MomentumChip token={intel.momentum} label={intel.momentum_label} size="sm" testid="flow-intel-momentum" />
        </div>

        {/* Probability + Health */}
        <div className="grid grid-cols-2 gap-3 mb-4">
          <div className="rounded-xl p-3" style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.06)" }} data-testid="flow-intel-probability">
            <ProbRing value={intel.exchange_probability} label="Exchange prob" sub={intel.exchange_window?.label || "—"} />
          </div>
          <div className="rounded-xl p-3 flex flex-col justify-center" style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.06)" }} data-testid="flow-intel-health">
            <p className="text-[10px] uppercase tracking-[0.20em] font-bold" style={{ color: "#C4B5FD" }}>Deal Health</p>
            <div className="flex items-end gap-2 mt-1">
              <span className="font-bold text-[28px] tabular-nums leading-none text-white">{intel.deal_health}</span>
              <span className="text-[11px] mb-1" style={{ color: "rgba(148,163,184,0.85)" }}>/100</span>
            </div>
            <div className="mt-2"><RiskTrend trend={intel.risk_trend} delta={intel.risk_trend_delta} /></div>
          </div>
        </div>

        {/* Operational tracking */}
        <div className="space-y-1 divide-y" style={{ borderColor: "rgba(255,255,255,0.05)" }}>
          <StatRow
            icon={Users}
            label="Solicitor response"
            sub={sol.last_response_label ? `Last reply ${sol.last_response_label}` : "No replies yet"}
            valueNode={<Score value={`${sol.score ?? 0}`} label={sol.label || "—"} tone={sol.tone || "low"} />}
          />
          <StatRow
            icon={Activity}
            label="Buyer engagement"
            sub={buyer.last_active_label ? `Active ${buyer.last_active_label}` : "No activity yet"}
            valueNode={<Score value={`${buyer.score ?? 0}`} label={buyer.label || "—"} tone={buyer.tone || "low"} />}
          />
          {intel.mortgage_expiry && (
            <StatRow
              icon={KeyRound}
              label="Mortgage offer"
              sub="Expiry countdown"
              valueNode={
                <span className="text-[11.5px] font-bold tabular-nums"
                  style={{ color: TONE[intel.mortgage_expiry.tone].fg }}>
                  {intel.mortgage_expiry.label}
                </span>
              }
            />
          )}
          <StatRow
            icon={Link2}
            label="Chain stability"
            sub={chain.label}
            valueNode={
              <div className="flex items-center gap-1.5">
                <TinyRing value={chain.score} color={chain.score >= 75 ? "#10B981" : chain.score >= 55 ? "#8B5CF6" : "#F59E0B"} />
                <span className="text-[12px] font-bold tabular-nums text-white">{chain.score ?? 0}</span>
              </div>
            }
          />
          {intel.exchange_window && (
            <StatRow
              icon={Clock}
              label="Exchange window"
              sub={intel.completion_window ? `Completion · ${intel.completion_window.label}` : "Targeted"}
              valueNode={
                <span className="text-[11.5px] font-bold tabular-nums text-white">
                  {intel.exchange_window.label}
                </span>
              }
            />
          )}
        </div>

        {/* Operational alerts */}
        {intel.operational_alerts?.length > 0 && (
          <div className="mt-4">
            <div className="flex items-center justify-between mb-2">
              <p className="text-[10px] uppercase tracking-[0.20em] font-bold" style={{ color: "#C4B5FD" }}>
                Flow Signals™ · {intel.operational_alerts.length}
              </p>
              <FlowFreshenedTimestamp at={intel.generated_at} />
            </div>
            <ul className="space-y-1.5">
              {intel.operational_alerts.map((a) => (
                <Alert key={a.id} alert={a} isNew={newAlertIds.has(a.id)} />
              ))}
            </ul>
          </div>
        )}

        {/* CTA */}
        {deal?.id && (
          <a
            href={`/insights`}
            data-testid="flow-intel-view-all"
            className="mt-4 w-full h-9 rounded-lg inline-flex items-center justify-between px-3 text-white text-[12px] font-bold transition-transform hover:-translate-y-0.5"
            style={{
              background: "linear-gradient(135deg, #8B5CF6 0%, #6366F1 100%)",
              boxShadow: "0 8px 22px -8px rgba(99,102,241,0.55), inset 0 1px 0 rgba(255,255,255,0.16)",
            }}
          >
            See all intelligence <ArrowUpRight size={12} />
          </a>
        )}
      </div>
    </section>
  );
};

/** Soft "Updated just now" timestamp for the dark FlowIntelligencePanel.
 * Re-keyed per minute so the label gracefully transitions (just now → 1m → 2m)
 * with a brief opacity freshening on each tick. No theatre. */
const FlowFreshenedTimestamp = ({ at }) => {
  const [tick, setTick] = React.useState(0);
  React.useEffect(() => {
    const i = setInterval(() => setTick((t) => t + 1), 30_000);
    return () => clearInterval(i);
  }, []);
  const label = React.useMemo(() => {
    if (!at) return "";
    const ms = Date.now() - new Date(at).getTime();
    if (ms < 30_000) return "Updated just now";
    if (ms < 60_000) return "Updated <1m";
    if (ms < 3_600_000) return `Updated ${Math.floor(ms / 60_000)}m ago`;
    return "";
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [at, tick]);
  if (!label) return null;
  return (
    <span
      key={`${at}|${tick}`}
      data-testid="flow-intel-freshened"
      className="text-[10px] cf-freshen tabular-nums"
      style={{ color: "rgba(196,181,253,0.7)" }}
    >
      {label}
    </span>
  );
};


/** Live "Emma is typing…" pulse — used above the FlowComposer. */
export const TypingIndicator = ({ typing = [] }) => {
  if (!typing.length) return null;
  const first = typing[0];
  const others = typing.length - 1;
  const text = others > 0
    ? `${first.full_name} and ${others} other${others === 1 ? "" : "s"} typing…`
    : `${first.full_name} is typing…`;
  return (
    <div data-testid="flow-typing-indicator" className="flex items-center gap-2 text-[11px] text-slate-500 px-1 mb-1">
      <span className="inline-flex gap-0.5">
        <span className="w-1 h-1 rounded-full bg-indigo-400 cf-typing-dot" style={{ animationDelay: "0ms" }} />
        <span className="w-1 h-1 rounded-full bg-indigo-400 cf-typing-dot" style={{ animationDelay: "150ms" }} />
        <span className="w-1 h-1 rounded-full bg-indigo-400 cf-typing-dot" style={{ animationDelay: "300ms" }} />
      </span>
      <span className="italic">{text}</span>
    </div>
  );
};
